import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { AttachFile, AutoAwesome, Close } from '@mui/icons-material';

import { IKiGrundbuchProposal, kiReadsDocuments } from '../Interfaces/IKi';
import {
  useAnalyseGrundbuch,
  useGetKiStatus,
} from '../hooks/reactQuerys/KiQueries';
import { fileToAttachment, validateAttachmentFile } from '../Ki/kiAttachment';
import { KiGrundbuchProposalCard } from '../Ki/KiGrundbuchProposalCard';

interface IProps {
  open: boolean;
  onClose: () => void;
  /**
   * Bereits vorliegende Datei — etwa das Dokument aus dem Posteingang. Ist sie
   * gesetzt, entfällt die Auswahl: der Benutzer hat die Datei ja schon vor
   * sich, ein zweites Mal danach zu fragen wäre eine Zumutung.
   */
  initialFile?: File | null;
}

/**
 * Ein Grundbuchblatt aus einem Auszug anlegen (#1336).
 *
 * Datei wählen → auslesen → prüfen → übernehmen. Die Prüfstation ist dieselbe
 * Karte, die auch im Chat erscheint, und sie öffnet denselben Blatt-Editor —
 * ein zweiter Weg zum selben Ziel wäre ein zweiter Ort, an dem sich Fehler
 * einnisten.
 *
 * Der Hinweis zum Datenabfluss steht **vor** dem Auslesen und nicht in den
 * Einstellungen: er gehört dorthin, wo die Datei ausgewählt wird.
 */
export const GrundbuchAuszugImportDialog: React.FC<IProps> = ({
  open,
  onClose,
  initialFile = null,
}) => {
  const { kiStatus } = useGetKiStatus();
  const { analyseGrundbuch, isPendingAnalyseGrundbuch } = useAnalyseGrundbuch();

  const [file, setFile] = useState<File | null>(initialFile);
  const [proposal, setProposal] = useState<IKiGrundbuchProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(initialFile);
    setProposal(null);
    setError(null);
  };

  // Eine nachgereichte Datei übernehmen — der Dialog kann geöffnet sein,
  // bevor das Dokument geladen ist.
  useEffect(() => {
    if (initialFile !== null) {
      setFile(initialFile);
    }
  }, [initialFile]);

  function handlePick(files: FileList | null) {
    const picked = files?.[0];

    if (picked === undefined) {
      return;
    }

    const problem = validateAttachmentFile(picked, ['application/pdf']);

    if (problem !== null) {
      setError(problem);

      return;
    }

    setError(null);
    setProposal(null);
    setFile(picked);
  }

  async function handleAnalyse() {
    if (file === null) {
      return;
    }

    setError(null);

    try {
      const result = await analyseGrundbuch({
        Attachment: await fileToAttachment(file),
      });
      const grundstuecke = (result.Draft.GrundstueckArray ?? []) as Record<
        string,
        unknown
      >[];
      const zaehle = (key: string) =>
        grundstuecke.reduce(
          (sum, gs) => sum + ((gs[key] as unknown[] | undefined)?.length ?? 0),
          0
        );

      // Auf die Form der Chat-Karte bringen: dieselbe Darstellung, dieselbe
      // Übernahme — nur die Quelle ist eine andere.
      setProposal({
        Kind: 'grundbuchblatt',
        Label: `Grundbuchblatt ${String(result.Draft.Blattnummer ?? '?')}`,
        Payload: result.Draft,
        idGrundbuchblatt: result.idGrundbuchblatt,
        Display: {
          Amtsgericht: result.AmtsgerichtText,
          Gemarkung: result.GemarkungText,
          EigentuemerArray: result.EigentuemerArray,
          AnzahlGrundstuecke: grundstuecke.length,
          AnzahlAbteilung2: zaehle('Abteilung2Array'),
          AnzahlAbteilung3: zaehle('Abteilung3Array'),
          Confidence: result.Confidence,
          Reason: result.Reason,
        },
        Hinweise: [],
      });
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== '' ? detail : 'Der Auszug konnte nicht ausgelesen werden.'
      );
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isPendingAnalyseGrundbuch ? undefined : onClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <AutoAwesome fontSize='small' color='primary' />
          <span>Anlegen über Grundbuchauszug</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {kiStatus?.Ready !== true && (
            <Alert severity='warning'>
              Die KI-Anbindung ist nicht eingerichtet. Ohne sie lässt sich ein
              Auszug nicht auslesen.
            </Alert>
          )}

          {kiStatus?.Ready === true && !kiReadsDocuments(kiStatus) && (
            <Alert severity='warning'>
              {kiStatus.ProviderLabel ?? 'Der eingestellte KI-Anbieter'} kann
              Dateien nicht inhaltlich lesen. Ein anderer Anbieter wird dafür
              nicht angesprochen.
            </Alert>
          )}

          <Alert severity='info'>
            Der Auszug wird <b>vollständig</b> an{' '}
            {kiStatus?.ProviderLabel ?? 'den eingerichteten Anbieter'}{' '}
            übertragen — mit Eigentümern, Belastungen und Gläubigern. Bitte nur
            Auszüge hochladen, die dorthin gelangen dürfen.
          </Alert>

          <input
            ref={fileInputRef}
            type='file'
            hidden
            accept='application/pdf'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handlePick(event.target.files);
              event.target.value = '';
            }}
          />

          <Stack direction='row' spacing={1} alignItems='center'>
            <Button
              variant='outlined'
              startIcon={<AttachFile />}
              disabled={isPendingAnalyseGrundbuch}
              onClick={() => fileInputRef.current?.click()}
            >
              PDF auswählen
            </Button>
            {file !== null && (
              <Chip
                label={file.name}
                onDelete={isPendingAnalyseGrundbuch ? undefined : reset}
              />
            )}
          </Stack>

          {isPendingAnalyseGrundbuch && (
            <Stack direction='row' spacing={1} alignItems='center'>
              <CircularProgress size={18} />
              <Typography variant='body2' color='text.secondary'>
                Der Auszug wird gelesen. Bei vielen Seiten dauert das eine
                Minute — das Fenster bitte offen lassen.
              </Typography>
            </Stack>
          )}

          {error !== null && <Alert severity='error'>{error}</Alert>}

          {proposal !== null && <KiGrundbuchProposalCard proposal={proposal} />}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          color='inherit'
          startIcon={<Close />}
          disabled={isPendingAnalyseGrundbuch}
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Schließen
        </Button>
        <Button
          variant='contained'
          startIcon={<AutoAwesome />}
          disabled={
            file === null ||
            isPendingAnalyseGrundbuch ||
            !kiReadsDocuments(kiStatus) ||
            proposal !== null
          }
          onClick={() => void handleAnalyse()}
        >
          Auslesen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
